import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useSettings } from "@/context/SettingsContext";

const BRAND = "Warehouse Coffee";
const DEFAULT_IMAGE = "/og-image.jpg";
const DEFAULT_DESC =
  "Warehouse Coffee (ESTD 2020) is an Indonesian Arabica green coffee supplier and exporter. Indonesian Arabica Ateng and other green beans, warehouse-stored and prepared for international buyers.";

export const PAGE_SEO = {
  "/": {
    title: "Warehouse Coffee | Indonesian Arabica Green Coffee Supplier & Exporter",
    description: DEFAULT_DESC,
  },
  "/our-coffee": {
    title: "Our Coffee - Indonesian Arabica Green Beans | Warehouse Coffee",
    description: "Indonesian Arabica green coffee from Warehouse Coffee, including our main product Indonesian Arabica Ateng. Specifications, processing and grades for importers and roasters.",
  },
  "/available-stock": {
    title: "Available Green Coffee Stock | Warehouse Coffee",
    description: "Current Indonesian Arabica green coffee stock: quantities, grades, process and packaging. Download the stock & price sheet (PDF or Excel) or request a quote.",
  },
  "/coffee-origin": {
    title: "Coffee Origin - Indonesian Arabica Producing Regions | Warehouse Coffee",
    description: "Where our Indonesian Arabica green coffee comes from: producing regions, altitude, varieties and processing at origin.",
  },
  "/quality": {
    title: "Quality Control - Green Coffee Inspection | Warehouse Coffee",
    description: "How Warehouse Coffee inspects Indonesian green coffee: moisture, defects, screen size and physical quality checks before export.",
  },
  "/export-logistics": {
    title: "Export & Logistics - Green Coffee Shipping from Indonesia | Warehouse Coffee",
    description: "Export terms, packaging, container options, documentation and shipping for Indonesian Arabica green coffee buyers worldwide.",
  },
  "/our-warehouse": {
    title: "Our Warehouse - Green Coffee Storage in Indonesia | Warehouse Coffee",
    description: "Inside the Warehouse Coffee facility: green coffee storage, sorting, preparation and loading in Indonesia.",
  },
  "/export-journey": {
    title: "Export Journey - From Sourcing to Buyer Delivery | Warehouse Coffee",
    description: "Step by step, how Indonesian green coffee moves from sourcing and inspection to packing, container loading and international shipment.",
  },
  "/gallery": {
    title: "Gallery - Warehouse, Loading & Export Photos | Warehouse Coffee",
    description: "Real photos from the Warehouse Coffee warehouse, green coffee preparation, loading and export shipments.",
  },
  "/about": {
    title: "About Us - Indonesian Green Coffee Exporter Since 2020 | Warehouse Coffee",
    description: "Warehouse Coffee is an Indonesian green coffee business established in 2020, sourcing, preparing and supplying Arabica coffee to domestic and international buyers.",
  },
  "/request-quote": {
    title: "Request a Quote - Indonesian Green Coffee | Warehouse Coffee",
    description: "Send a request for quotation for Indonesian Arabica green coffee. Ask for prices, samples and specification sheets.",
  },
  "/contact": {
    title: "Contact Warehouse Coffee - Indonesian Green Coffee Supplier",
    description: "Contact Warehouse Coffee by WhatsApp, phone or email for Indonesian Arabica green coffee supply and export inquiries.",
  },
};

const absUrl = (u) => {
  if (!u) return "";
  if (/^(https?:|data:)/i.test(u)) return u;
  return `${window.location.origin}${u.startsWith("/") ? "" : "/"}${u}`;
};

const setMeta = (attr, key, content) => {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content || "");
};

const setLink = (rel, href) => {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const setJsonLd = (id, data) => {
  let el = document.getElementById(id);
  if (!data) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
};

export const applySeo = ({ title, description, image, path, noindex = false, type = "website" }) => {
  const desc = description || DEFAULT_DESC;
  const url = `${window.location.origin}${path || window.location.pathname}`;
  // data: URLs cannot be fetched by social crawlers, fall back to the real warehouse photo
  const img = image && !/^data:/i.test(image) ? absUrl(image) : absUrl(DEFAULT_IMAGE);
  document.title = title || PAGE_SEO["/"].title;
  setMeta("name", "description", desc);
  setMeta("name", "robots", noindex ? "noindex, nofollow" : "index, follow");
  setMeta("property", "og:type", type);
  setMeta("property", "og:site_name", BRAND);
  setMeta("property", "og:title", document.title);
  setMeta("property", "og:description", desc);
  setMeta("property", "og:url", url);
  setMeta("property", "og:image", img);
  setMeta("property", "og:image:alt", image && !/^data:/i.test(image) ? document.title : "Green coffee sacks stored at the Warehouse Coffee warehouse in Indonesia");
  setMeta("name", "twitter:card", "summary_large_image");
  setMeta("name", "twitter:title", document.title);
  setMeta("name", "twitter:description", desc);
  setMeta("name", "twitter:image", img);
  setLink("canonical", url);
};

/** Per-page SEO for dynamic pages (e.g. product detail). */
export default function Seo(props) {
  const { title, description, image, path, noindex, type } = props;
  useEffect(() => {
    applySeo({ title, description, image, path, noindex, type });
  }, [title, description, image, path, noindex, type]);
  return null;
}

/** Route-level SEO for all static pages + site-wide structured data and favicon. */
export function RouteSeo() {
  const { pathname } = useLocation();
  const { settings } = useSettings();

  useEffect(() => {
    if (pathname.startsWith("/product/")) return; // ProductDetail sets its own tags
    if (pathname.startsWith("/admin") || pathname.startsWith("/auth")) {
      applySeo({ title: `Admin | ${BRAND}`, description: DEFAULT_DESC, path: pathname, noindex: true });
      return;
    }
    const cfg = PAGE_SEO[pathname] || PAGE_SEO["/"];
    applySeo({ ...cfg, path: pathname, noindex: !PAGE_SEO[pathname] });
  }, [pathname]);

  useEffect(() => {
    if (settings.favicon) setLink("icon", settings.favicon);
    const sameAs = [settings.instagram, settings.facebook, settings.linkedin].filter((u) => /^https?:/i.test(u || ""));
    const logo = settings.logo && !/^data:/i.test(settings.logo) ? absUrl(settings.logo) : absUrl("/logo.jpg");
    setJsonLd("ld-organization", {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: settings.company_name || "WAREHOUSE COFFEE",
      alternateName: BRAND,
      url: window.location.origin,
      logo,
      image: absUrl(DEFAULT_IMAGE),
      description: settings.description || DEFAULT_DESC,
      foundingDate: settings.established_year || "2020",
      address: { "@type": "PostalAddress", addressCountry: "ID", ...(settings.address ? { streetAddress: settings.address } : {}) },
      ...(settings.phone || settings.email
        ? {
            contactPoint: {
              "@type": "ContactPoint",
              contactType: "sales",
              ...(settings.phone ? { telephone: settings.phone } : {}),
              ...(settings.email ? { email: settings.email } : {}),
              availableLanguage: ["English", "Indonesian"],
            },
          }
        : {}),
      ...(sameAs.length ? { sameAs } : {}),
    });
  }, [settings]);

  return null;
}
